import { gql } from "@apollo/client";
import {
  studentFragment,
  staffPeriodListFragment,
  staffNameFragment,
  selectedPeriodFragment,
} from "./TimetablePlannerFragments";

export const getClassTimetablePeriodsQuery = gql`
  query getClassTimetablePeriods(
    $ids: [ID!]
    $periodsFilters: PeriodFilterOptions
    $permissionFilters: TimetableSlotPermissionFilters
    $termFilters: TermFilters
    $attendanceFilters: TimetableSlotAttendanceFilters!
    $itemFilter: TimetableSlotItemsFilters
  ) {
    multiNode(ids: $ids, type: COURSE) {
      ... on Course {
        id
        periodsV3(filters: $periodsFilters) {
          edges {
            node {
              ...selectedPeriodItemV2
            }
          }
        }
      }
    }
  }
  ${selectedPeriodFragment.selectedPeriodsV2}
`;

export const getCalendarEventsQuery = gql`
  query getCalendarEvents(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: UserCoursesCalendarFilter!
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        id
        calendarEvents(filters: $filters) {
          id
          startTime
          endTime
          itemType
          item {
            id
            ... on CalendarEventItem {
              id
              label
              eventType
              isFullDayEvent
              tag: eventTag {
                id
                label
                colorCode
                uid
              }
            }
            ... on CalendarEvent {
              id
              title
              start: startDate
              end: endDate
              tag {
                id
                label
                colorCode
                uid
              }
            }
          }
        }
      }
      ... on Student {
        id
        calendarEvents(filters: $filters) {
          id
          startTime
          endTime
          itemType
          item {
            id
            ... on CalendarEventItem {
              id
              label
              isFullDayEvent
              tag: eventTag {
                id
                label
                colorCode
                uid
              }
            }
            ... on CalendarEvent {
              id
              title
              start: startDate
              end: endDate
              tag {
                id
                label
                colorCode
                uid
              }
            }
          }
        }
      }
    }
  }
`;

export const getTeacherTimetablePeriodsQuery = gql`
  query getTeacherTimetablePeriods(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $periodsFilters: PeriodFilterOptions
    $permissionFilters: TimetableSlotPermissionFilters
    $termFilters: TermFilters
    $attendanceFilters: TimetableSlotAttendanceFilters!
    $itemFilter: TimetableSlotItemsFilters
  ) {
    node(id: $id, type: $type) {
      ... on Student {
        ...studentPeriodListItemV2
      }
      ... on Staff {
        ...staffPeriodListItemV2
      }
    }
  }
  ${studentFragment.studentPeriodListV2}
  ${staffPeriodListFragment.staffPeriodListV2}
`;

export const getRoutineDetailsQuery = gql`
  query getRoutineDetails(
    $id: ID!
    $curriculumProgramIds: [ID!]
    $routineFilters: RoutineFilterOptions!
    $routineDatesFilters: RoutineDatesFilters!
    $fetchBellSchedule: Boolean!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        curriculumPrograms(curriculumIds: $curriculumProgramIds) {
          id
          routines(filter: $routineFilters) {
            id
            label
            routineMode
            routineDates(filters: $routineDatesFilters) {
              date
              rotationDay {
                id
                label
              }
              bellSchedule @include(if: $fetchBellSchedule) {
                id
                periodsEdge {
                  periods {
                    period {
                      id
                      label
                      type
                      abbreviation
                    }
                    startTime
                    endTime
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationTeachersQuery = gql`
  query getOrganizationTeachers(
    $organizationId: ID!
    $archivalState: ARCHIVAL_STATE_ENUM
    $curriculumProgramIds: [ID!]
    $academicYearIds: [ID!]
    $searchText: String
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        staff(
          filters: {
            archivalState: $archivalState
            curriculumProgramIds: $curriculumProgramIds
            academicYearIds: $academicYearIds
            searchText: $searchText
          }
        ) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              ...staffNameFields
              profileImage
              email
            }
          }
        }
      }
    }
  }
  ${staffNameFragment.staffNameFields}
`;

export const getUserCoursesQuery = gql`
  query getUserCourses(
    $id: ID!
    $filters: CourseFilter
    $courseGradeFilters: CourseGradeFilters
  ) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        courses(filters: $filters) {
          id
          title
          primaryGrade(filters: $courseGradeFilters) {
            id
            name
          }
        }
      }
    }
  }
`;
